import classNames from 'classnames';
import styles from './styles.module.scss';
import { Icons } from 'pages/Quotes/Icons';
import { useDispatch, useSelector } from 'react-redux';
import { setOtherRolesQuotesTabIndexAction } from 'store/actions/quotes';
import { quoteRequestStatus, quotesStatusesMap } from 'settings/constants/quotes';
import { getUserRoleSelector } from 'store/selectors/user';
import { CLIENT, THIRD_PARTY } from 'settings/constants/roles';
import { useHistory, useLocation } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { LocationService } from 'services';
import { setQuotesArchiveEffect } from 'store/effects/quotes';
import {
  getGroupedQuotesSelector,
  getOtherRolesQuotesTabIndexSelector,
} from 'store/selectors/quotes';

const locationSrv = new LocationService();

const TabsHeader = () => {
  const dispatch = useDispatch();
  const tabIndex = useSelector(getOtherRolesQuotesTabIndexSelector);
  const { byStatus } = useSelector(getGroupedQuotesSelector);
  const userRole = useSelector(getUserRoleSelector);
  const history = useHistory();
  const location = useLocation();
  locationSrv.setLocation(location);
  const { groupId } = useSelector(getGroupedQuotesSelector);
  const [quotesList, setQuotesList] = useState<any[]>([]);
  const { src } = locationSrv.getQuery();

  useEffect(() => {
    if (!src)
      history.push(locationSrv.setQuery({ groupId: userRole === CLIENT ? 'Received' : 'New' }));
    return () => {
      dispatch(setOtherRolesQuotesTabIndexAction(0));
    };
  }, []);

  useEffect(() => {
    if (groupId === 'Archived') dispatch(setQuotesArchiveEffect(true));
    else dispatch(setQuotesArchiveEffect(false));
  }, [groupId]);

  useEffect(() => {
    if (groupId !== 'Archived') setQuotesList(byStatus);
  }, [byStatus, groupId]);

  const getCount = (status) =>
    quotesList?.[status]?.filter((quote) => !quote.IsArchived).length || 0;

  const getPendingCount = (status) =>
    quotesList?.[status]?.filter(
      (quote) =>
        quote?.Status === quoteRequestStatus.PendingAnswer &&
        !quote.IsArchived &&
        quote?.DateViewedByPartner,
    ).length || 0;

  const getNewCount = (status) =>
    quotesList?.[status]?.filter(
      (quote) =>
        quote?.Status === quoteRequestStatus.PendingAnswer &&
        !quote.IsArchived &&
        !quote?.DateViewedByPartner,
    ).length || 0;

  const draftCount = getCount(quotesStatusesMap(userRole)[quoteRequestStatus.Draft]);

  const existingNewCount =
    quotesList?.[quotesStatusesMap(userRole)[quoteRequestStatus.New]]?.filter(
      (quote) => !quote.IsArchived && quote?.Status === quoteRequestStatus.New,
    ).length || 0;

  const newCount = getNewCount(quotesStatusesMap(userRole)[quoteRequestStatus.PendingAnswer]);

  const pendingCount = getPendingCount(
    quotesStatusesMap(userRole)[quoteRequestStatus.PendingAnswer],
  );

  const respondedCount = getCount(quotesStatusesMap(userRole)[quoteRequestStatus.Responded]);

  const getClientTabs = () => {
    const totalClientReceivedCount = respondedCount;
    const totalClientPendingCount = pendingCount + newCount + existingNewCount || 0;
    const totalClientDraftCount = draftCount || 0;

    return [
      {
        label: `Received${totalClientReceivedCount ? ` (${totalClientReceivedCount})` : ''}`,
        icon: (
          <Icons
            variant={Icons.RECEIVED}
            color={tabIndex === 0 ? '#262626' : '#747475'}
            containerized={false}
          />
        ),
      },
      {
        label: `Pending${totalClientPendingCount ? ` (${totalClientPendingCount})` : ''}`,
        icon: (
          <Icons
            variant={Icons.PENDING}
            color={tabIndex === 1 ? '#262626' : '#747475'}
            containerized={false}
          />
        ),
      },
      {
        label: `Drafts${totalClientDraftCount ? ` (${totalClientDraftCount})` : ''}`,
        icon: (
          <Icons
            variant={Icons.DRAFT}
            color={tabIndex === 2 ? '#262626' : '#747475'}
            containerized={false}
          />
        ),
      },
      {
        label: 'Archive',
        icon: (
          <Icons
            variant={Icons.ARCHIVE}
            color={tabIndex === 3 ? '#262626' : '#747475'}
            containerized={false}
          />
        ),
      },
    ];
  };

  const getVendorTabs = () => {
    const totalVendorQuotedCount = respondedCount;
    const totalVendorPendingCount = pendingCount || 0;
    const totalVendorNewCount = newCount + existingNewCount;

    return [
      {
        label: `New${totalVendorNewCount ? ` (${totalVendorNewCount})` : ''}`,
        icon: (
          <Icons
            variant={Icons.NEW_QUOTE}
            color={tabIndex === 0 ? '#262626' : '#747475'}
            containerized={false}
          />
        ),
      },
      {
        label: `Pending${totalVendorPendingCount ? ` (${totalVendorPendingCount})` : ''}`,
        icon: (
          <Icons
            variant={Icons.PENDING}
            color={tabIndex === 1 ? '#262626' : '#747475'}
            containerized={false}
          />
        ),
      },
      {
        label: `Quoted${totalVendorQuotedCount ? ` (${totalVendorQuotedCount})` : ''}`,
        icon: (
          <Icons
            variant={Icons.QUOTED}
            color={tabIndex === 2 ? '#262626' : '#747475'}
            containerized={false}
          />
        ),
      },
      {
        label: 'Archive',
        icon: (
          <Icons
            variant={Icons.ARCHIVE}
            color={tabIndex === 3 ? '#262626' : '#747475'}
            containerized={false}
          />
        ),
      },
    ];
  };

  const handleTabClick = (index) => {
    dispatch(setOtherRolesQuotesTabIndexAction(index));

    if (userRole === CLIENT) {
      switch (index) {
        case 0:
          history.push(locationSrv.setQuery({ groupId: 'Received' }));
          break;
        case 1:
          history.push(locationSrv.setQuery({ groupId: 'Pending' }));
          break;
        case 2:
          history.push(locationSrv.setQuery({ groupId: 'Drafts' }));
          break;
        case 3:
          history.push(locationSrv.setQuery({ groupId: 'Archived' }));
          break;
        default:
          history.push(locationSrv.setQuery({ groupId: 'Received' }));
      }
    } else if (userRole === THIRD_PARTY) {
      switch (index) {
        case 0:
          history.push(locationSrv.setQuery({ groupId: 'New' }));
          break;
        case 1:
          history.push(locationSrv.setQuery({ groupId: 'Pending Response' }));
          break;
        case 2:
          history.push(locationSrv.setQuery({ groupId: 'Quoted' }));
          break;
        case 3:
          history.push(locationSrv.setQuery({ groupId: 'Archived' }));
          break;
        default:
          history.push(locationSrv.setQuery({ groupId: 'New' }));
      }
    }
  };

  return (
    <div className={styles.tabsHeader}>
      {(userRole === CLIENT ? getClientTabs() : getVendorTabs()).map((tab, index) => (
        <div
          className={classNames(styles.tab, tabIndex === index ? styles.tabActive : undefined)}
          onClick={() => handleTabClick(index)}
        >
          {tab.icon}
          <span
            className={classNames(
              styles.tabLabel,
              tabIndex === index ? styles.tabLabelActive : undefined,
            )}
          >
            {tab.label}
          </span>
        </div>
      ))}
    </div>
  );
};

export default TabsHeader;
